import { memo, useState, useMemo, ReactNode } from "react";
import { useSelector } from "react-redux";
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse, useMediaQuery } from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HandshakeTwoToneIcon from "@mui/icons-material/HandshakeTwoTone";
import DescriptionIcon from "@mui/icons-material/DescriptionTwoTone";
import { Stack, Typography, useFinanceableTheme, Box } from "@financeable-com-au/financeable-ui";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector, assetSelector, userDetailsSelector } from "@/store/slices/applicationFormSlice";
import type { ApplicationState, Customer, Entity, Asset } from "@/store/slices/types/applicationFormSlice";
import {
  APPLICATION_SECTIONS_ID,
  applicationPurpose,
  getIsAssetLoan,
  getIsCommercialOrBusinessApplication,
  getIsConsumerOrPersonalApplication,
} from "@/constants";
import { useDemoCommercialApplication } from "@/hooks";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "../Utils/helperFunction";

// Define the theme type - using a permissive type that accepts the actual theme from useFinanceableTheme
type FinanceableTheme = {
  palette: {
    common: {
      black: string;
      white: string;
      [key: string]: unknown;
    };
    text: {
      primary: string;
      secondary: string;
      [key: string]: unknown;
    };
    primary: {
      main: string;
      dark: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

interface ApplicationMenuProps {
  onItemClick?: (sectionId: string) => void;
}

interface ScrollableListProps {
  theme: FinanceableTheme;
  children: ReactNode;
  itemCount: number;
  maxItemsBeforeScroll?: number;
}

interface CollapsibleMenuSectionProps {
  theme: FinanceableTheme;
  title: string;
  icon: ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  isActive: boolean;
  children: ReactNode;
  itemCount: number;
}

interface SingleMenuItemProps {
  theme: FinanceableTheme;
  title: string;
  icon: ReactNode;
  sectionId: string;
  itemId: string;
  isActive: boolean;
  onClick: (sectionId: string, itemId: string) => void;
}

interface SubMenuItemProps {
  theme: FinanceableTheme;
  primary: string;
  secondary?: string;
  itemId: string;
  sectionId: string;
  isActive: boolean;
  onClick: (sectionId: string, itemId: string) => void;
}

interface SectionType {
  assets: boolean;
  customers: boolean;
  entities: boolean;
}

interface ApplicationMenuProps {
  isApplicationMenuOpen: boolean;
  toggleApplicationMenu: () => void;
}
// Reusable ScrollableList component
const ScrollableList = memo(({ theme, children, itemCount, maxItemsBeforeScroll = 3 }: ScrollableListProps) => {
  const needsScroll = itemCount > maxItemsBeforeScroll;
  const { common, text } = theme.palette;

  return (
    <Box
      sx={{
        maxHeight: needsScroll ? "200px" : "none",
        overflowY: needsScroll ? "auto" : "visible",
        borderRadius: "8px",
        // Thin scrollbar for Firefox
        scrollbarWidth: "thin",
        // Thin scrollbar for Webkit browsers (Chrome, Safari, Edge)
        "&::-webkit-scrollbar": {
          width: "6px !important",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.primary.main || "rgba(0, 0, 0, 0.3)",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: theme.palette.primary.dark || "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      {children}
    </Box>
  );
});

// Reusable CollapsibleMenuSection component
const CollapsibleMenuSection = memo(
  ({ theme, title, icon, isExpanded, onToggle, isActive, children, itemCount }: CollapsibleMenuSectionProps) => {
    const { common, text } = theme.palette;

    const menuItemStyle = {
      backgroundColor: isActive ? common.black : "transparent",
      color: isActive ? common.white : text.primary,
      "&:hover": {
        backgroundColor: isActive ? common.black : "rgba(0, 0, 0, 0.04)",
      },
      padding: "7px 5px",
      margin: "8px 0",
      borderRadius: "8px",
    };

    const iconStyle = {
      color: isActive ? common.white : text.secondary,
      minWidth: "35px",
    };

    return (
      <>
        <ListItem disablePadding>
          <ListItemButton onClick={onToggle} sx={menuItemStyle}>
            <ListItemIcon sx={iconStyle}>{icon}</ListItemIcon>
            <ListItemText primary={title} />
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <ScrollableList theme={theme} itemCount={itemCount}>
            <List component="div" disablePadding>
              {children}
            </List>
          </ScrollableList>
        </Collapse>
      </>
    );
  },
);

// Reusable SingleMenuItem component
const SingleMenuItem = memo(({ theme, title, icon, sectionId, itemId, isActive, onClick }: SingleMenuItemProps) => {
  const { common, text } = theme.palette;
  const menuItemStyle = {
    backgroundColor: isActive ? common.black : "transparent",
    color: isActive ? common.white : text.primary,
    "&:hover": {
      backgroundColor: isActive ? common.black : "rgba(0, 0, 0, 0.04)",
    },
    padding: "15px 5px",
    borderRadius: "8px",
  };

  const iconStyle = {
    color: isActive ? common.white : text.secondary,
    minWidth: "35px",
  };

  return (
    <ListItem disablePadding>
      <ListItemButton onClick={() => onClick(sectionId, itemId)} sx={menuItemStyle}>
        <ListItemIcon sx={iconStyle}>{icon}</ListItemIcon>
        {/* <ListItemText primary={title} /> */}
        <Typography level="body-md" style={{ color: isActive ? common.white : text.primary }}>
          {title}
        </Typography>
      </ListItemButton>
    </ListItem>
  );
});

// Reusable SubMenuItem component
const SubMenuItem = memo(({ theme, primary, secondary, itemId, sectionId, isActive, onClick }: SubMenuItemProps) => {
  const { common, text } = theme.palette;

  const subMenuItemStyle = {
    backgroundColor: isActive ? "rgba(0, 0, 0, 0.08)" : "transparent",
    color: isActive ? text.primary : text.secondary,
    "&:hover": {
      backgroundColor: isActive ? "rgba(0, 0, 0, 0.12)" : "rgba(0, 0, 0, 0.04)",
    },
    paddingLeft: "40px",
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "center",
    borderRadius: "8px",
  };

  return (
    <ListItem disablePadding style={{ margin: "8px 0" }}>
      <ListItemButton onClick={() => onClick(sectionId, itemId)} sx={subMenuItemStyle}>
        <Typography level="body-md">{primary}</Typography>
        {secondary && <Typography level="body-xs">{secondary}</Typography>}
      </ListItemButton>
    </ListItem>
  );
});

// Utility function for smooth scrolling
const scrollToSection = (sectionId: string): void => {
  const possibleIds = [sectionId, `#${sectionId}`];
  let element: HTMLElement | null = null;

  for (const id of possibleIds) {
    element = document.getElementById(id.replace("#", ""));
    if (element) break;
  }

  if (element) {
    const headerOffset = 80; // Offset for fixed header
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

// Reusable CountBadge component
interface CountBadgeProps {
  theme: FinanceableTheme;
  count: number;
  position?: "top" | "bottom";
}

const CountBadge = memo(({ theme, count, position = "bottom" }: CountBadgeProps) => {
  if (count <= 0) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        [position]: "4px",
        right: "4px",
        backgroundColor: theme.palette.primary["200"] || theme.palette.primary?.main,
        color: theme.palette.common.white,
        borderRadius: "4px",
        minWidth: "16px",
        height: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: 600,
        padding: "0 4px",
      }}
    >
      {count}
    </Box>
  );
});

// Reusable MinimizedMenuItem component (with badge)
interface MinimizedMenuItemProps {
  theme: FinanceableTheme;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
  count?: number;
  badgePosition?: "top" | "bottom";
}

const MinimizedMenuItem = memo(
  ({ theme, icon, isActive, onClick, count, badgePosition = "bottom" }: MinimizedMenuItemProps) => {
    return (
      <Box
        onClick={onClick}
        sx={{
          width: "48px",
          height: "48px",
          borderRadius: "8px",
          backgroundColor: isActive ? theme.palette.common.black : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          position: "relative",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: isActive ? theme.palette.common.black : "rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <Box
          sx={{
            color: isActive ? theme.palette.common.white : theme.palette.text.secondary,
            fontSize: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        {count !== undefined && <CountBadge theme={theme} count={count} position={badgePosition} />}
      </Box>
    );
  },
);

// Reusable MinimizedSingleMenuItem component (without badge)
interface MinimizedSingleMenuItemProps {
  theme: FinanceableTheme;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const MinimizedSingleMenuItem = memo(({ theme, icon, isActive, onClick }: MinimizedSingleMenuItemProps) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: "48px",
        height: "48px",
        borderRadius: "8px",
        backgroundColor: isActive ? theme.palette.common.black : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: isActive ? theme.palette.common.black : "rgba(0, 0, 0, 0.06)",
        },
      }}
    >
      <Box
        sx={{
          color: isActive ? theme.palette.common.white : theme.palette.text.secondary,
          fontSize: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
    </Box>
  );
});

// Reusable MinimizedMenuExpandButton component
interface MinimizedMenuExpandButtonProps {
  onClick: () => void;
}

const MinimizedMenuExpandButton = memo(({ onClick }: MinimizedMenuExpandButtonProps) => {
  return (
    <Stack
      onClick={onClick}
      sx={{
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.06)",
        borderRadius: "8px",
        padding: "8px 12px",
        cursor: "pointer",
        minWidth: "40px",
        minHeight: "40px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.12)",
          transform: "scale(1.05)",
        },
        "&:active": {
          transform: "scale(0.95)",
        },
      }}
    >
      <ExpandMoreIcon sx={{ transform: "rotate(270deg)" }} />
    </Stack>
  );
});

// Reusable ExpandedMenuCollapseButton component
interface ExpandedMenuCollapseButtonProps {
  onClick: () => void;
}

const ExpandedMenuCollapseButton = memo(({ onClick }: ExpandedMenuCollapseButtonProps) => {
  return (
    <Stack
      onClick={onClick}
      sx={{
        position: "absolute",
        bottom: 20,
        right: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.06)",
        borderRadius: "8px",
        padding: "8px 12px",
        cursor: "pointer",
        minWidth: "40px",
        minHeight: "40px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.12)",
          transform: "scale(1.05)",
        },
        "&:active": {
          transform: "scale(0.95)",
        },
      }}
    >
      <ExpandLessIcon sx={{ transform: "rotate(270deg)" }} />
    </Stack>
  );
});

const ApplicationMenu = memo(({ isApplicationMenuOpen, toggleApplicationMenu }: ApplicationMenuProps) => {
  const theme = useFinanceableTheme() as unknown as FinanceableTheme;
  const { isDemoCommercialApplication } = useDemoCommercialApplication();
  const { ASSETS, LOAN_DETAILS, INTRODUCER_DECLARATION, LOAN_SUMMARY } = APPLICATION_SECTIONS_ID;
  const application = useSelector(applicationSelector) as ApplicationState | null | undefined;
  const asset = useSelector(assetSelector) as Asset | null | undefined;
  const customerRole = getCustomerRole(application?.applicationType, application?.entities || [], application?.purpose);
  const isCommercialOrBusinessApplication = application?.applicationType
    ? getIsCommercialOrBusinessApplication(application?.applicationType)
    : false;
  const isConsumerOrPersonalApplication = application?.applicationType
    ? getIsConsumerOrPersonalApplication(application?.applicationType)
    : false;
  const isAssetLoan = application?.applicationType ? getIsAssetLoan(application?.applicationType) : false;
  const userDetails = useSelector(userDetailsSelector) as {
    application?: ApplicationState;
    [key: string]: unknown;
  };

  const [expandedSections, setExpandedSections] = useState<{
    assets: boolean;
    customers: boolean;
    entities: boolean;
  }>({
    assets: true,
    customers: false,
    entities: false,
  });

  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  // Get all assets (primary + additional)
  const allAssets = useMemo(() => {
    const assets = [];
    if (asset && typeof asset === "object" && "_id" in asset) {
      assets.push(asset);
    }
    if (userDetails?.application?.additionalAssets && userDetails.application.additionalAssets.length > 0) {
      assets.push(...userDetails.application.additionalAssets);
    }
    return assets;
  }, [asset, userDetails?.application?.additionalAssets]);

  // Get customers
  const customers = useMemo(() => {
    return (application?.customers as Customer[]) || [];
  }, [application?.customers]);

  // Get entities
  const entities = useMemo(() => {
    return (application?.entities as Entity[]) || [];
  }, [application?.entities]);

  const handleToggleSection = (section: keyof SectionType) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleItemClick = (sectionId: string, itemId: string) => {
    setSelectedItem(itemId);
    scrollToSection(sectionId);

    // if (onItemClick) {
    //   onItemClick(sectionId);
    // }
  };

  const isSectionActive = (sectionKey: string) => {
    return selectedItem?.includes(sectionKey) ? true : false;
  };

  const isSmallScreen = useMediaQuery("(max-width: 1200px)");

  return (
    <>
      {isApplicationMenuOpen ? (
        <Box
          style={{
            marginTop: "30px",
            padding: "0 10px 0 10px",
            maxWidth: "240px",
            ...(!isSmallScreen ? { minWidth: "240px" } : {}),
            height: "100vh",
          }}
        >
          <Typography level="title-md" style={{ marginBottom: 20 }}>
            Application Menu
          </Typography>

          <List sx={{ padding: 0 }}>
            {/* Assets Section */}
            {!isDemoCommercialApplication && isAssetLoan && (
              <CollapsibleMenuSection
                theme={theme}
                title="Assets"
                icon={<DirectionsCarIcon />}
                isExpanded={expandedSections.assets}
                onToggle={() => handleToggleSection("assets")}
                isActive={isSectionActive("asset")}
                itemCount={allAssets?.length || 0}
              >
                {allAssets.length > 0 &&
                  allAssets.map((assetItem, index) => {
                    const assetId = `asset-${assetItem._id || index}`;
                    const assetName = assetItem.assetType || `Asset ${index + 1}`;

                    return (
                      <SubMenuItem
                        theme={theme}
                        key={assetId}
                        primary={assetName}
                        itemId={assetId}
                        sectionId={assetId}
                        isActive={selectedItem === assetId}
                        onClick={handleItemClick}
                      />
                    );
                  })}
              </CollapsibleMenuSection>
            )}

            {/* Loan Summary */}
            {isDemoCommercialApplication && (
              <SingleMenuItem
                theme={theme}
                title="Summary"
                icon={<DescriptionIcon />}
                sectionId={LOAN_SUMMARY}
                itemId={LOAN_SUMMARY}
                isActive={selectedItem === LOAN_SUMMARY}
                onClick={handleItemClick}
              />
            )}

            {/* Loan Details */}
            <SingleMenuItem
              theme={theme}
              title="Loan Details"
              icon={<RequestQuoteIcon />}
              sectionId={LOAN_DETAILS}
              itemId={LOAN_DETAILS}
              isActive={selectedItem === LOAN_DETAILS}
              onClick={handleItemClick}
            />

            {/* Entity Section */}
            {isCommercialOrBusinessApplication && (
              <CollapsibleMenuSection
                theme={theme}
                title="Entity"
                icon={<BusinessIcon />}
                isExpanded={expandedSections.entities}
                onToggle={() => {
                  handleToggleSection("entities");
                  if (entities?.length <= 0) {
                    handleItemClick("addEntity", "addEntity");
                  }
                }}
                isActive={isSectionActive("entity")}
                itemCount={entities?.length || 0}
              >
                {entities?.length > 0 &&
                  entities.map((entity: Entity, index: number) => {
                    const entityId = `entity-${entity._id || index}`;
                    const sectionId = `entity-${entity._id}`;
                    const entityName = entity.entityName || `Entity ${index + 1}`;

                    return (
                      <SubMenuItem
                        theme={theme}
                        key={entityId}
                        primary={entityName}
                        itemId={entityId}
                        sectionId={sectionId}
                        isActive={selectedItem === entityId}
                        onClick={handleItemClick}
                      />
                    );
                  })}
              </CollapsibleMenuSection>
            )}

            {/* Customers Section */}
            <CollapsibleMenuSection
              theme={theme}
              title={customerRole}
              icon={<PersonIcon />}
              isExpanded={expandedSections.customers}
              onToggle={() => {
                handleToggleSection("customers");
                if (customers?.length <= 0) {
                  handleItemClick(`add${customerRole}`, `customer`);
                }
              }}
              isActive={isSectionActive("customer")}
              itemCount={customers?.length || 0}
            >
              {customers?.length > 0 &&
                customers.map((customer: Customer, index: number) => {
                  const customerRoleId = `customer-${customer._id || index}`;
                  const isNameExists = customer?.firstName || customer?.lastName;
                  const customerName = isNameExists
                    ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim()
                    : `${customerRole} ${index + 1}`;

                  return (
                    <SubMenuItem
                      theme={theme}
                      key={customerRoleId}
                      primary={customerName}
                      itemId={customerRoleId}
                      sectionId={customerRoleId}
                      isActive={selectedItem === customerRoleId}
                      onClick={handleItemClick}
                    />
                  );
                })}
            </CollapsibleMenuSection>

            {/* Introducer & NCCP Declaration both are single combine into nccp */}
            {isConsumerOrPersonalApplication && (
              <SingleMenuItem
                theme={theme}
                title="NCCP"
                icon={<HandshakeTwoToneIcon />}
                sectionId={INTRODUCER_DECLARATION}
                itemId={INTRODUCER_DECLARATION}
                isActive={selectedItem === INTRODUCER_DECLARATION}
                onClick={handleItemClick}
              />
            )}
          </List>

          <ExpandedMenuCollapseButton onClick={toggleApplicationMenu} />
        </Box>
      ) : (
        <Box
          sx={{
            marginTop: "10px",
            padding: "10px 8px",
            width: "64px",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
          }}
        >
          {/* Assets Icon */}
          {!isDemoCommercialApplication && isAssetLoan && (
            <MinimizedMenuItem
              theme={theme}
              icon={<DirectionsCarIcon />}
              isActive={isSectionActive("asset")}
              onClick={() => handleItemClick(ASSETS, ASSETS)}
              count={allAssets?.length || 0}
            />
          )}

          {/* Summary Icon */}
          {isDemoCommercialApplication && (
            <MinimizedSingleMenuItem
              theme={theme}
              icon={<DescriptionIcon />}
              isActive={selectedItem === LOAN_SUMMARY}
              onClick={() => handleItemClick(LOAN_SUMMARY, LOAN_SUMMARY)}
            />
          )}

          {/* Loan Details Icon */}
          <MinimizedSingleMenuItem
            theme={theme}
            icon={<RequestQuoteIcon />}
            isActive={selectedItem === LOAN_DETAILS}
            onClick={() => handleItemClick(LOAN_DETAILS, LOAN_DETAILS)}
          />

          {/* Entity Icon */}
          {isCommercialOrBusinessApplication && (
            <MinimizedMenuItem
              theme={theme}
              icon={<BusinessIcon />}
              isActive={isSectionActive("entity")}
              onClick={() => handleItemClick("addEntity", "addEntity")}
              count={entities.length}
            />
          )}

          {/* Customers Icon */}
          <MinimizedMenuItem
            theme={theme}
            icon={<PersonIcon />}
            isActive={isSectionActive("customer")}
            onClick={() => handleItemClick(`add${customerRole}`, `customer`)}
            count={customers.length}
          />

          {/* NCCP Icon */}
          {isConsumerOrPersonalApplication && (
            <MinimizedSingleMenuItem
              theme={theme}
              icon={<HandshakeTwoToneIcon />}
              isActive={selectedItem === INTRODUCER_DECLARATION}
              onClick={() => handleItemClick(INTRODUCER_DECLARATION, INTRODUCER_DECLARATION)}
            />
          )}

          {/* Expand Button */}
          <MinimizedMenuExpandButton onClick={toggleApplicationMenu} />
        </Box>
      )}
    </>
  );
});

export default ApplicationMenu;
